import api from './api'

export interface BillingPlanFeature {
  title?: string
  tagline?: string
  broadcast_enabled?: boolean
  ai_auto_reply?: boolean
  monthly_ai_replies?: number
  [key: string]: any
}

export interface PlanCardFeatureItem {
  text: string
  included: boolean
  highlight?: boolean
}

export interface BillingPlanDisplayMetadata {
  title?: string
  subtitle?: string
  mode?: 'standard' | 'featured'
  badge?: string | null
  base_amount_subunits?: number
  original_amount_subunits?: number
  price_note?: string
  display_order?: number
  icon_name?: string
  button_text?: string
  features_header?: string
  features?: PlanCardFeatureItem[]
}

export interface BillingPlan {
  plan_type: string
  sku: string
  amount_subunits: number
  currency: string
  billing_months: number
  features: BillingPlanFeature
  display_metadata?: BillingPlanDisplayMetadata
}

export interface ListBillingPlansResponse {
  plans: BillingPlan[]
}

export interface CreatePaymentPayload {
  payment_type: 'subscription' | 'one_time_plan' | 'topup'
  plan_type?: string
  billing_months?: number
  amount_subunits?: number
  topup_units?: number
}

export interface CreatePaymentResponse {
  transaction_id: string
  payment_type: string
  checkout_type: 'subscription' | 'order'
  checkout_id: string
  key_id: string
  plan_type?: string
  billing_months?: number
  amount_subunits: number
  currency: string
  topup_units?: number
  topup_validity_months?: number
  prefill?: {
    name?: string
    email?: string
    contact?: string
  }
}

export interface AIQuotaBucket {
  id: string
  bucket_type: string
  source_type: string
  remaining_units: number
  total_units: number
  active_from: string
  expires_at: string
}

export interface PaymentStatusResponse {
  transaction_id: string
  plan_type: string
  billing_months?: number
  status: 'pending' | 'paid' | 'active' | 'expired' | 'failed'
  billing_cycle_start?: string | null
  billing_cycle_end?: string | null
  subscription_id?: string | null
  quota_buckets?: AIQuotaBucket[]
}

export interface CancelSubscriptionResponse {
  status: string
  provider_status: string
  smb_id: string
  subscription_id: string
  billing_cycle_end?: string | null
}

export interface AbandonPaymentResponse {
  transaction_id: string
  plan_type: string
  record_type: string
  status: string
  abandoned: boolean
  account_released?: boolean
}

export interface ActiveSubscription {
  id: string
  smb_id: string
  plan_type: string
  billing_months: number
  status: string
  starts_at: string
  ends_at: string
  razorpay_subscription_id?: string | null
  auto_renew?: boolean
  billing_plan?: BillingPlan | null
}

export interface BillingHistoryItem {
  transaction_id: string
  plan_type: string
  amount_subunits: number
  currency?: string
  status: string
  created_at: string
}

export interface EffectiveEntitlements {
  unlimited_broadcast_enabled?: boolean
  auto_reply_enabled?: boolean
}

export interface BillingSummaryResponse {
  smb_id: string
  subscriptions?: ActiveSubscription[]
  active_subscriptions: ActiveSubscription[]
  quota_buckets?: AIQuotaBucket[]
  ai_quota_buckets: AIQuotaBucket[]
  billing_history: BillingHistoryItem[]
  has_active_growth_subscription: boolean
  has_active_subscription: boolean
  effective_entitlements?: EffectiveEntitlements
  total_ai_replies_remaining?: number
}

export const paymentsApi = {
  getBillingPlans: async (): Promise<ListBillingPlansResponse> => {
    const response = await api.get('/billing/plans')
    return response.data
  },

  createPayment: async (smbId: string, payload: CreatePaymentPayload): Promise<CreatePaymentResponse> => {
    const response = await api.post(`/smbs/${smbId}/payments/create`, payload)
    return response.data
  },

  getPaymentStatus: async (transactionId: string): Promise<PaymentStatusResponse> => {
    const response = await api.get(`/transactions/${transactionId}/status`)
    return response.data
  },

  abandonPayment: async (transactionId: string): Promise<AbandonPaymentResponse> => {
    const response = await api.post(`/transactions/${transactionId}/abandon`)
    return response.data
  },

  getBillingSummary: async (smbId: string): Promise<BillingSummaryResponse> => {
    const response = await api.get(`/smbs/${smbId}/billing/summary`)
    const raw = response.data || {}
    const subs = raw.subscriptions || raw.active_subscriptions || []
    const quotaBuckets = raw.quota_buckets || raw.ai_quota_buckets || []
    const entitlements = raw.effective_entitlements || {}

    const hasActiveSub =
      raw.has_active_subscription ??
      (subs.length > 0 || entitlements.broadcast_enabled === true || entitlements.auto_reply_enabled === true)

    const hasGrowthSubscription =
      raw.has_active_growth_subscription ??
      (subs.some((s: any) => s.plan_type?.includes('growth')) || entitlements.auto_reply_enabled === true)

    return {
      smb_id: raw.smb_id || smbId,
      subscriptions: subs,
      active_subscriptions: subs,
      quota_buckets: quotaBuckets,
      ai_quota_buckets: quotaBuckets,
      billing_history: raw.billing_history || [],
      has_active_subscription: Boolean(hasActiveSub),
      has_active_growth_subscription: Boolean(hasGrowthSubscription),
      effective_entitlements: entitlements,
      total_ai_replies_remaining: raw.total_ai_replies_remaining,
    }
  },

  cancelSubscription: async (smbId: string, subscriptionId?: string): Promise<CancelSubscriptionResponse> => {
    const response = await api.post(`/smbs/${smbId}/billing/subscription/cancel`, null, {
      params: { subscription_id: subscriptionId },
    })
    return response.data
  },
}

export default paymentsApi
